/**
 * Google Analytics 4 page tracking. The gtag tag itself (measurement id
 * G-V03RZ5DF58) is loaded by public/index.html — with its automatic
 * page_view turned off — so this app, being a single-page app, reports every
 * page view itself, once per route.
 *
 * Privacy: the page URL sent to GA is rebuilt from an allowlist of
 * marketing/attribution params only. The real URLs can carry identifiers that
 * must never reach Google — AsiaCell's return trip adds the carrier's opaque
 * `userId` plus `redirectUrl`/`correlationId`, Zain's callbacks carry their
 * own params, and any MSISDN/OTP-shaped value could appear — so everything
 * not explicitly listed below is dropped rather than trying to enumerate what
 * to hide. `gclid`/`gbraid`/`wbraid` are kept deliberately: GA4 and Google
 * Ads need them in the page URL to attribute conversions.
 */

const GA_ALLOWED_PARAMS = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_term",
  "utm_content",
  "gclid",
  "gbraid",
  "wbraid",
  "fbclid",
  "ttclid",
  "campaignId",
  "campaignid",
  "trackerId",
  "trackerid",
  "lang",
];

let lastTrackedPath = null;

/** Origin + path + only the allowlisted query params. */
export function buildAnalyticsLocation(pathname, search) {
  const source = new URLSearchParams(search || "");
  const kept = new URLSearchParams();

  GA_ALLOWED_PARAMS.forEach((key) => {
    if (source.has(key)) kept.set(key, source.get(key));
  });

  const query = kept.toString();

  return `${window.location.origin}${pathname}${query ? `?${query}` : ""}`;
}

/**
 * Sends one GA4 `page_view` per pathname change. A search-only change on the
 * same path (e.g. a param being added) is not a new page view. No-ops when
 * gtag isn't there (localhost, an ad blocker, or the script not loaded yet —
 * gtag() queues into dataLayer, so an early call is still delivered).
 */
export function trackPageView(pathname, search, pageTitle) {
  if (typeof window === "undefined" || typeof window.gtag !== "function") return;
  if (pathname === lastTrackedPath) return;

  lastTrackedPath = pathname;

  try {
    window.gtag("event", "page_view", {
      page_location: buildAnalyticsLocation(pathname, search),
      page_path: pathname,
      page_title: pageTitle || document.title,
    });
  } catch {
    // Analytics must never break the page.
  }
}
